import React, { useEffect, useMemo, useState, useRef } from "react";
import { Table, Tooltip, Modal, message, Button, Image } from "antd";
import { ColumnsType } from "antd/es/table";
import { useExternalBillModel } from "../externalBillModel";
import { IExternalBillField } from "../externalBillType";
import { bigNumber, momentFormat, repairFormatNum } from "@/utils/utils";
import useNavModel from "@/model/navModel";
import { getQrcodeUrl, tableSize } from "@/utils/constants";
import { roleColumnModel } from "@/utils/roleColumn";
import useGlobalModel from "@/model/globalModel";
import YhTootip from "@/components/YhTootip";
import YhTag from "@/components/YhTag";
import { useDictModel } from "@/views/base/dict/dictModel";
import html2canvas from "html2canvas"; // 引入截图库
import logo from "../../../../assets/images/logo1.png";
import { getBaseRequestUrl } from "../../../../utils/constants";
import { externalBillService } from "../externalBillService";
import QuotationModal from "./QuotationModal"; // 引入报价完成弹窗组件
import { ApprovalModal } from "./checkAcceptModel";
import { CompleteModal } from "./completeModal";
import { QRCodeSVG } from 'qrcode.react'
import { YhAuth } from "@/components/auth/YhAuth";
import { ResourceAuthEnum } from "@/types/VrmResoureAuthEnum";

type statusType = {
    "external_repair.init": string; // 初始状态
    "external_repair.send": string; // 已发布
    "external_repair.sendend": string; // 报价完成
    "external_repair.ing": string; // 审核中
    "external_repair.audited": string; // 审批通过
    "external_repair.reject": string; // 审批驳回
    "external_repair.complete": string; // 已完工

    [key: string]: any;
};

export function DataTable() {
    const { getTreeNodeName } = useDictModel();
    const {
        dataSource,
        selectedRowKeys,
        pgCfg,
        descRows,
        loadData,
        setSelectedRowKeys,
        setDescRows,
        reset,
        rowsDescTotal,
        descTotal,
        dispatchVisible,
        setDispatchVisible,
        checkAcceptVisible,
        setCheckAcceptVisible,
        currentCompleteBillId,
        setCurrentCompleteBillId,
        completeModalVisible,
        setCompleteModalVisible
    } = useExternalBillModel();
    const { selectedNavId, replaceNav, findNav } = useNavModel();
    const { user: { roles } } = useGlobalModel();
    const { getRoleColumns } = roleColumnModel();

    // 二维码相关状态
    const [qrcodeVisible, setQrcodeVisible] = useState(false);
    const [qrcodeUrl, setQrcodeUrl] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const qrRef = useRef<HTMLDivElement>(null); // 用于引用二维码容器

    // 报价完成弹窗相关状态
    const [quotationModalVisible, setQuotationModalVisible] = useState(false);
    const [currentBillId, setCurrentBillId] = useState<string>("");

    // 判断用户是否有审核权限
    const hasAuditPermission = (auditRole: string) => {
        return roles.includes(auditRole);
    };

    const statusColor: statusType = {
        "external_repair.init": "blue", // 初始状态
        "external_repair.send": "orange", // 已发布
        "external_repair.sendend": "green", // 报价完成
        "external_repair.ing": "cyan", // 审核中
        "external_repair.audited": "green", // 审批通过
        "external_repair.reject": "red", // 审批驳回
        "external_repair.complete": "gold", // 已完工
    };

    const onLook = (code: string) => {
        let supplierEditKey = "20-3";
        replaceNav(
            selectedNavId,
            supplierEditKey,
            { id: code, mode: "look" },
            true
        );
    };

    useEffect(() => {
        loadData();
    }, []);

    const onRow = (record: IExternalBillField) => {
        let { id } = record as { id: string };
        let findIndexDesc = descRows.findIndex((item) => (item.id === id));
        let newDescRows = [...descRows];
        let newSelectedRowKeys = [...selectedRowKeys];

        if (findIndexDesc === -1) {
            newDescRows.push(record);
            setDescRows(newDescRows);
            newSelectedRowKeys.push(id);
            setSelectedRowKeys(newSelectedRowKeys);
        } else {
            newDescRows.splice(findIndexDesc, 1);
            setDescRows(newDescRows);
            newSelectedRowKeys.splice(findIndexDesc, 1);
            setSelectedRowKeys(newSelectedRowKeys);
        }
    };

    // 发布账单并生成二维码
    const handlePublish = async (record: IExternalBillField) => {
        const { id, code } = record;
        let newUrl = getQrcodeUrl()
        const targetUrl = `${newUrl}/send.html?order=${id}`;

        // 显示确认模态框
        Modal.confirm({
            title: "确认发布",
            content: `确定要发布外修单 ${code} 吗？`,
            okText: "确认",
            okType: "primary",
            cancelText: "取消",
            onOk: async () => {
                setLoading(true);
                try {
                    // 模拟发布接口调用
                    const res = await externalBillService.externalRepairSend(id);
                    if (res && res.success) {
                        message.success("发布成功");
                        setQrcodeUrl(targetUrl);
                        setQrcodeVisible(true);
                        // 重新加载数据
                        loadData();
                    } else {
                        message.error(res.retMsg || "发布失败");
                    }
                } catch (error: any) {
                    message.error(`发布失败: ${error.retMsg}`);
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    // 处理报价完成确认
    const handleQuotationConfirm = (selectedCompany: any) => {
        message.success(`已选择报价公司: ${selectedCompany.supplierName}`);
        // 重新加载数据
        loadData();
    };

    // 操作列渲染函数
    const renderOperationButtons = (record: IExternalBillField) => {
        const { status, auditRole, id } = record;
        const buttons = [];

        // 查看按钮（始终显示）
        buttons.push(
            <YhAuth resCode={ResourceAuthEnum.VRM2_EXTERNALREPAIR_DETIAL}>
                <button
                    key="view"
                    style={{
                        backgroundColor: "#4165e317",
                        color: "#4165E3",
                        border: "1px solid #4165E3",
                        borderRadius: "2px",
                        padding: "0 4px",
                        fontSize: '12px',
                        cursor: "pointer",
                        transition: "all 0.2s",
                        outline: "none",
                        marginRight: "8px",
                    }}
                    onClick={() => onLook(record.id as string)}
                >
                    详情
                </button>
            </YhAuth>
        );

        // 进度按钮（始终显示）
        buttons.push(
            <YhAuth resCode={ResourceAuthEnum.VRM2_EXTERNALREPAIR_DETIALLOG}>
                <button
                    key="progress"
                    style={{
                        backgroundColor: "#ff980017",
                        color: "orange",
                        border: "1px solid orange",
                        borderRadius: "2px",
                        fontSize: '12px',
                        padding: "0 4px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        outline: "none",
                        marginRight: "8px",
                    }}
                    onClick={() => {
                        setSelectedRowKeys([record.id as string]);
                        setDispatchVisible(true);
                    }}
                >
                    进度
                </button>
            </YhAuth>
        );

        // 根据状态添加其他按钮
        switch (status) {
            case "external_repair.init":
                buttons.push(
                    <YhAuth resCode={ResourceAuthEnum.VRM2_EXTERNALREPAIR_PUBLISH}>
                        <button
                            key="publish"
                            style={{
                                backgroundColor: "#2196f326",
                                color: "#2196F3",
                                border: "1px solid #2196F3",
                                borderRadius: "2px",
                                fontSize: '12px',
                                padding: "0 4px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                outline: "none",
                                marginRight: "8px",
                            }}
                            onClick={() => handlePublish(record)} // 绑定发布事件
                        >
                            发布
                        </button>
                    </YhAuth>
                );
                break;

            case "external_repair.send":
                buttons.push(
                    <YhAuth resCode={ResourceAuthEnum.VRM2_EXTERNALREPAIR_QUOTATION}>
                        <button
                            key="quotation"
                            style={{
                                backgroundColor: "#faad1417",
                                color: "#faad14",
                                border: "1px solid #faad14",
                                borderRadius: "2px",
                                fontSize: '12px',
                                padding: "0 4px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                outline: "none",
                                marginRight: "8px",
                            }}
                            onClick={() => {
                                setCurrentBillId(record.id as string);
                                setQuotationModalVisible(true);
                            }} // 绑定报价完成事件
                        >
                            报价完成
                        </button>
                    </YhAuth>
                );
                buttons.push(
                    <YhAuth resCode={ResourceAuthEnum.VRM2_EXTERNALREPAIR_LOOKRCODE}>
                        <button
                            key="qrcode"
                            style={{
                                backgroundColor: "#00be872e",
                                color: "#00BE87",
                                border: "1px solid #00BE87",
                                fontSize: '12px',
                                borderRadius: "2px",
                                padding: "0 4px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                outline: "none",
                            }}
                            onClick={() => {
                                let newUrl = getQrcodeUrl()
                                const targetUrl = `${newUrl}/send.html?order=${id}`;
                                console.log(newUrl)
                                console.log(targetUrl)
                                debugger
                                setQrcodeUrl(targetUrl);
                                setQrcodeVisible(true);
                            }}
                        >
                            查看二维码
                        </button>
                    </YhAuth>
                );
                break;

            case "external_repair.sendend":
            case "external_repair.ing":
                if (hasAuditPermission(auditRole)) {
                    buttons.push(
                        <button
                            key="reject"
                            style={{
                                backgroundColor: "#ff704329",
                                color: "#FF7043",
                                border: "1px solid #FF7043",
                                fontSize: '12px',
                                borderRadius: "2px",
                                padding: "0 4px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                outline: "none",
                                marginRight: "8px",
                            }}
                            onClick={() => {
                                setSelectedRowKeys([(record.id as string)])
                                setCheckAcceptVisible(true)
                            }}
                        >
                            审批
                        </button>
                    );
                }
                break;

            case "external_repair.audited":
                buttons.push(
                    <YhAuth resCode={ResourceAuthEnum.VRM2_EXTERNALREPAIR_COMPLETE}>
                        <button
                            key="complete"
                            style={{
                                backgroundColor: "#ef53501a",
                                color: "#EF5350",
                                border: "1px solid #EF5350",
                                fontSize: '12px',
                                borderRadius: "2px",
                                padding: "0 4px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                outline: "none",
                            }}
                            onClick={() => {
                                setCurrentCompleteBillId(record);
                                setCompleteModalVisible(true);
                            }}
                        >
                            完工
                        </button>
                    </YhAuth>
                );
                break;
        }

        return buttons;
    };

    const columns: ColumnsType<any> = [
        {
            title: "操作",
            width: 200,
            fixed: "left",
            align: "center",
            render: (text, record) => (
                <div style={{ whiteSpace: "nowrap" }}>
                    {renderOperationButtons(record)}
                </div>
            ),
        },
        {
            title: "序号",
            fixed: "left",
            width: 60,
            render: (t, r, i) => i + 1,
        },
        {
            title: "外修单号",
            width: 120,
            dataIndex: "code",
            render: (text: any, record: any) => <YhTootip text={text} style={{ width: 120 }} />,
        },
        {
            title: "单据状态",
            width: 80,
            dataIndex: "status",
            render: (text) => <YhTag color={statusColor[text]} text={getTreeNodeName(text)} />,
        }, {
            title: "工单单号",
            width: 150,
            dataIndex: "transferCode",
            render: (text: any, record: any) => <YhTootip text={text} style={{ width: 150 }} />,
        },
        {
            title: "客户名称",
            width: 130,
            dataIndex: "customerName",
            render: (text: any, record: any) => <YhTootip text={text} style={{ width: 130 }} />,
        },
        {
            title: "车辆牌照",
            width: 100,
            dataIndex: "licensePlate",
            render: (text: any) => <YhTootip text={text} style={{ width: 100 }} />,
        },
        {
            title: '车辆品牌',
            width: 160,
            dataIndex: 'brandName',
            render: (text: any) => <YhTootip text={text} style={{ width: '160px' }} />,
        },
        {
            title: '车辆类型',
            width: 150,
            dataIndex: 'vehicleTypeName',
            render: (text: any) => <YhTootip text={text} style={{ width: '150px' }} />,
        },
        {
            title: "报修人",
            width: 200,
            dataIndex: "contact",
        },
        {
            title: "报修电话",
            width: 120,
            dataIndex: "phone",
        },
        {
            title: "工时费",
            width: 100,
            dataIndex: "itemAmount",
            render: (text) => text ? `￥${repairFormatNum(text)}` : '---'
        },
        {
            title: "配件费",
            width: 100,
            dataIndex: "materialAmount",
            render: (text) => text ? `￥${repairFormatNum(text)}` : '---'
        },
        {
            title: "合计金额",
            width: 100,
            dataIndex: "licensePlate",
            render: (text, res) => text ? `￥${repairFormatNum(bigNumber.add(res.materialAmount ?? 0, res.itemAmount ?? 0))}` : '---'
        },
        {
            title: "外修单位",
            width: 140,
            dataIndex: "supplierName",
            render: (text: any) => text ? <YhTootip text={text} style={{ width: 140 }} /> : '---',
        },
        {
            title: "制单人",
            width: 140,
            dataIndex: "createUser",
        },
        {
            title: "制单时间",
            width: 140,
            dataIndex: "createTime",
            render: (text: any) => text ? momentFormat(text) : "",
        },
        {
            title: "完工时间",
            width: 140,
            dataIndex: "endTime",
            render: (text: any) => text ? momentFormat(text) : "",
        },
        {
            title: "完工人",
            width: 140,
            dataIndex: "checkUser",
        },
        {
            title: "预计完工时间",
            width: 150,
            dataIndex: "expectedCompletionTime",
            render: (text: any, record: any) => {
                // 格式化时间
                const formattedTime = (record.expectedCompletionTime || record.externalExpectedCompletionTime) ? momentFormat(record.externalExpectedCompletionTime ?? record.expectedCompletionTime) : '';
                // 判断是否需要标红
                const shouldHighlight = formattedTime && isWithin24Hours(formattedTime, record);

                return (
                    <span style={{ color: shouldHighlight ? "#EF5350" : "" }}>
                        {formattedTime}
                    </span>
                );
            }
        },
        {
            title: '所属站点',
            width: 250,
            fixed: "right",
            dataIndex: 'shopName',
            render: (text: any) => <YhTootip text={text} style={{ width: 250 }} />,
        },
    ];
    const isWithin24Hours = (expectedTime: any, record: any) => {
        // 如果没有预计时间，不标红
        if (!expectedTime) return false;

        // 如果单据已完工，不标红
        const isCompleted = record.status === 'external_repair.complete';

        if (isCompleted) return false;

        // 转换为时间戳（毫秒）
        const expected = new Date(expectedTime).getTime();
        const now = new Date().getTime();
        const twentyFourHours = 24 * 60 * 60 * 1000; // 86400000毫秒

        // 时间在当前时间之后且小于24小时
        return expected > now && expected - now < twentyFourHours;
    }
    const roleColumns = useMemo(() => {
        const params = { "code": "stockOut", "roleCodes": roles };
        return getRoleColumns("stockOut", "主单", columns, params);
    }, [roles, dataSource]);

    const onRightClick = (record: IExternalBillField) => {
        setDescRows([record]);
        setSelectedRowKeys([record.id as string]);
    };

    // 保存二维码图片到电脑
    const handleSaveQRCode = async () => {
        if (!qrRef.current) return;

        try {
            // 使用html2canvas捕获二维码容器并生成图片
            const canvas = await html2canvas(qrRef.current, {
                scale: 2, // 提高图片清晰度
                useCORS: true, // 处理跨域图片
            });

            // 创建下载链接
            const imgData = canvas.toDataURL("image/png");
            const a = document.createElement("a");
            a.href = imgData;
            a.download = `外修单二维码_${new Date().getTime()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            message.success("二维码已保存");
        } catch (error) {
            console.error("保存二维码失败", error);
            message.error("保存二维码失败，请重试");
        }
    };

    return (
        <>
            <Table
                bordered
                size={tableSize}
                style={{ cursor: "pointer" }}
                rowKey={(record) => record.id as string}
                rowSelection={{
                    type: "checkbox",
                    onChange: (selectedRowKeys: React.Key[], selectedRows: IExternalBillField[]) => {
                        setSelectedRowKeys(selectedRowKeys);
                        setDescRows(selectedRows);
                    },
                    selectedRowKeys,
                }}
                columns={roleColumns}
                dataSource={dataSource}
                pagination={{
                    showSizeChanger: true,
                    ...pgCfg,
                    defaultPageSize: 25,
                    pageSizeOptions: ["15", "25", "50", "100", "200", "500"],
                    position: ["bottomRight"],
                    showTotal: () => `共${pgCfg.total}条`,
                    onChange: (pageNum, pageSize) => {
                        loadData({ pageNum, pageSize }).then();
                    },
                }}
                // onRow={(record) => ({
                //     onClick: () => onRow(record),
                //     onContextMenu: (e) => {
                //         e.preventDefault();
                //         onRightClick(record);
                //     },
                // })}
                scroll={{ x: "max-content", y: "calc(100vh - 370px)" }}
            />

            {/* 二维码模态框 */}
            <Modal
                title="外修单二维码"
                open={qrcodeVisible}
                onOk={handleSaveQRCode}
                onCancel={() => setQrcodeVisible(false)}
                width={400}
                okText="保存二维码"
            >
                <div ref={qrRef} style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px 0" }}>
                    <div style={{ position: "relative" }}>
                        <QRCodeSVG
                            value={qrcodeUrl}
                            size={200}
                            fgColor="#34383C"
                            title="外修单二维码"
                        />
                        {/* 中间图片 - 替换为你的图片路径 */}
                        <div style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: "white",
                            borderRadius: "4px",
                            padding: "8px",
                            width: 50,
                            height: 50,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                        }}>
                            <Image
                                src={logo}
                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            />
                        </div>
                    </div>
                </div>
                <div style={{ textAlign: "center", marginTop: "15px", color: "#666" }}>
                    <p>#请使用微信扫描二维码报价#</p>
                </div>
            </Modal>

            {/* 报价完成模态框 */}
            <QuotationModal
                visible={quotationModalVisible}
                onCancel={() => setQuotationModalVisible(false)}
                onConfirm={handleQuotationConfirm}
                billId={currentBillId}
            />
            <ApprovalModal />
            <CompleteModal
                visible={completeModalVisible}
                onCancel={() => setCompleteModalVisible(false)}
                onConfirm={() => {
                    // 处理完工确认逻辑
                    message.success("完工确认成功");
                    setCompleteModalVisible(false);
                    loadData(); // 重新加载数据
                }}
                billVal={currentCompleteBillId}
            />
        </>
    );
}